import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import {
  AuthService,
  CompaniesApiService,
  CurrentUserPayloadModel,
  DebounceConstants,
} from '@eagle/shared';
import { VacancyModel } from '../../vacancies/models';
import { EmployeeModel } from '../../employees/models';
import { MatchingApiService } from './matching-api.service';
import { EmployeeMatchingFiltersModel } from '../models/employee-matching-filters.model';
import { MatchingDrivingLicenseService } from './matching-driving-license.service';
import { VacanciesApiService } from '../../vacancies/services';
import { EmployeesApiService } from '../../employees/services';
import { CompanyModel } from '../../companies/models';
import {
  CompanyMatchingListItemModel,
  CompanyMatchingResultListItemModel,
  EmployeeMatchingResultListItemModel,
  EmployeeMatchingResultModel,
  MatchingTypeEnum,
  VacancyMatchingListItemModel,
} from '../models';
import { DEFAULT_MATCHING_FILTERS_CONFIG } from './matching.config';

@Injectable({
  providedIn: 'root',
})
export class MatchingDataService {
  private matchingDrivingLicenseService = inject(MatchingDrivingLicenseService);
  private authService = inject(AuthService);
  private vacanciesApiService = inject(VacanciesApiService);
  private employeesApiService = inject(EmployeesApiService);
  private companiesApiService = inject(CompaniesApiService);
  private matchingApiService = inject(MatchingApiService);

  private employeeId: string = null;
  private employeeData: EmployeeModel = null;
  private employeeResumeFilters: Params;

  private vacancyId: string = null;
  private vacancyData: VacancyModel = null;
  private vacancyResumeFilters: Params;

  private companyId: string = null;
  private companyData: CompanyModel = null;
  private companyResumeFilters: Params;

  private pseudoVacancyId: string = null;
  private pseudoVacancyCompanyId: string = null;

  private filters: EmployeeMatchingFiltersModel = {
    size: 100,
    page: 0,
  };

  private readonly _inMatchingProcess = new BehaviorSubject<boolean>(false);
  public readonly inMatchingProcess$ = this._inMatchingProcess.asObservable();
  private leadMatching = false;
  currentUserPayload: CurrentUserPayloadModel;

  private readonly lastMatchingPathKey: string = 'lastMatchingPath';
  private readonly matchingEntityListItemKeys: Record<MatchingTypeEnum, string> = {
    [MatchingTypeEnum.EMPLOYEE]: 'employee-matching-item',
    [MatchingTypeEnum.COMPANY]: 'company-matching-item',
    [MatchingTypeEnum.VACANCY]: 'vacancy-matching-item',
    [MatchingTypeEnum.COMPETENCE]: 'pseudo-vacancy-matching-item',
  };
  private readonly matchingEntityCacheKeys: Record<MatchingTypeEnum, string> = {
    [MatchingTypeEnum.EMPLOYEE]: 'employee-matching-cache-item',
    [MatchingTypeEnum.COMPANY]: 'company-matching-cache-item',
    [MatchingTypeEnum.VACANCY]: 'vacancy-matching-cache-item',
    [MatchingTypeEnum.COMPETENCE]: 'pseudo-vacancy-matching-cache-item',
  };
  private readonly matchingEntityCacheLinksKeys: Record<MatchingTypeEnum, string> = {
    [MatchingTypeEnum.EMPLOYEE]: 'employee-matching-cache-links',
    [MatchingTypeEnum.COMPANY]: 'company-matching-cache-links',
    [MatchingTypeEnum.VACANCY]: 'vacancy-matching-cache-links',
    [MatchingTypeEnum.COMPETENCE]: 'pseudo-vacancy-matching-cache-links',
  };

  get currentEmployeeId(): string {
    return this.employeeId;
  }
  get currentCompanyId(): string {
    return this.companyId;
  }
  get currentPseudoVacancyCompanyId(): string {
    return this.pseudoVacancyCompanyId;
  }

  constructor() {
    this.authService.currentUser$.subscribe(data => (this.currentUserPayload = data));

    if (Object.values(this.matchingEntityCacheKeys).some(key => !!this.getLocalStorageValue(key))) {
      this._inMatchingProcess.next(true);
    }
  }

  resetEmployeeId(): void {
    this.employeeId = null;
  }

  resetCompanyId(): void {
    this.companyId = null;
  }

  resetPseudoVacancyCompanyId(): void {
    this.pseudoVacancyCompanyId = null;
  }

  openEmployeeMatchingList(id: string): Observable<EmployeeModel> {
    if (this.employeeId !== id) {
      this.stopMatching();
      this.employeeId = id;
      this._inMatchingProcess.next(true);

      return this.loadEmployeeById(this.employeeId).pipe(
        tap(result => (this.employeeData = result))
      );
    } else {
      return of(this.employeeData);
    }
  }

  openCompanyMatchingList(id: string): Observable<CompanyModel> {
    if (this.companyId !== id) {
      this.stopMatching();
      this.companyId = id;
      this._inMatchingProcess.next(true);

      return this.loadCompanyById(this.companyId).pipe(tap(result => (this.companyData = result)));
    } else {
      return of(this.companyData);
    }
  }

  openPseudoVacancyMatchingList(id: string, pseudoVacancyId: string): Observable<CompanyModel> {
    if (this.pseudoVacancyId !== pseudoVacancyId) {
      this.stopMatching();
      this.pseudoVacancyId = pseudoVacancyId;
      this.pseudoVacancyCompanyId = id;
      this._inMatchingProcess.next(true);

      return this.loadCompanyById(this.pseudoVacancyCompanyId).pipe(
        tap(result => (this.companyData = result))
      );
    } else {
      return of(this.companyData);
    }
  }

  openVacancyMatchingList(id: string): Observable<VacancyModel> {
    if (this.vacancyId !== id) {
      this.stopMatching();
      this.vacancyId = id;
      this._inMatchingProcess.next(true);

      return this.loadVacancyById(this.vacancyId).pipe(tap(result => (this.vacancyData = result)));
    } else {
      return of(this.vacancyData);
    }
  }

  getEmployeeResumeFilters(): Params {
    return this.employeeResumeFilters;
  }

  setEmployeeResumeFilters(filters: Params): void {
    this.employeeResumeFilters = filters;
  }

  getCompanyResumeFilters(): Params {
    return this.companyResumeFilters;
  }

  setCompanyResumeFilters(filters: Params): void {
    this.companyResumeFilters = filters;
  }

  getVacancyResumeFilters(): Params {
    return this.vacancyResumeFilters;
  }

  setVacancyResumeFilters(filters: Params): void {
    this.vacancyResumeFilters = filters;
  }

  getEmployeeFilters() {
    const filters = this.getSessionValue(`employee-matching-filters-${this.employeeId}`) || {};
    return {
      ...this.filters,
      areaLatitude: this.employeeData?.residenceAddress?.latitude,
      areaLongitude: this.employeeData?.residenceAddress?.longitude,
      areaCity: this.employeeData?.residenceAddress?.city,
      areaRadius: filters.areaRadius || this.getEmployeeAreaRadius(),
      salesRegion: filters.salesRegion || this.getEmployeePostalCodes(),
      isConstructStatus: filters.isConstructStatus || this.getEmployeeConstructStatus(),
      drivingLicense:
        filters.drivingLicense ||
        this.matchingDrivingLicenseService.getDrivingLicenseForVacancy(
          this.employeeData?.drivingLicenses.map(l => l.drivingLicenseCode)
        ),
      freeText: filters.freeText || DEFAULT_MATCHING_FILTERS_CONFIG.freeText,
    };
  }

  getPseudoVacancyFilters() {
    const filters =
      this.getSessionValue(`pseudo-vacancy-matching-filters-${this.pseudoVacancyId}`) || {};
    const hasTransportWithNoneCode = this.employeeData?.transports?.some(
      transport => transport.transportCode === 'NONE'
    );
    return {
      ...this.filters,
      areaLatitude: this.companyData?.address?.latitude,
      areaLongitude: this.companyData?.address?.longitude,
      areaCity: this.companyData?.address?.city,
      drivingLicense: filters.drivingLicense || [],
      areaRadius: hasTransportWithNoneCode
        ? DEFAULT_MATCHING_FILTERS_CONFIG.areaRadius
        : filters.areaRadius || DEFAULT_MATCHING_FILTERS_CONFIG.areaRadius,
    };
  }

  getCompanyFilters() {
    const filters = this.getSessionValue(`company-matching-filters-${this.companyId}`) || {};
    return {
      ...this.filters,
      areaLatitude: this.companyData?.address?.latitude,
      areaLongitude: this.companyData?.address?.longitude,
      areaCity: this.companyData?.address?.city,
      drivingLicense: filters.drivingLicense || [],
      areaRadius: filters.areaRadius || DEFAULT_MATCHING_FILTERS_CONFIG.areaRadius,
    };
  }

  getVacancyFilters() {
    const filters = this.getSessionValue(`vacancy-matching-filters-${this.vacancyId}`) || {};
    const hasTransportWithNoneCode = this.employeeData?.transports?.some(
      transport => transport.transportCode === 'NONE'
    );
    return {
      ...this.filters,
      areaLatitude: this.vacancyData?.workAddress?.latitude,
      areaLongitude: this.vacancyData?.workAddress?.longitude,
      areaCity: this.vacancyData?.workAddress?.city,
      transport: filters.transport || [],
      drivingLicense:
        filters.drivingLicense ||
        this.matchingDrivingLicenseService.getDrivingLicenseForEmployee(
          this.vacancyData.drivingLicense ? [this.vacancyData.drivingLicense.code] : []
        ),
      areaRadius: hasTransportWithNoneCode
        ? DEFAULT_MATCHING_FILTERS_CONFIG.areaRadius
        : filters.areaRadius || DEFAULT_MATCHING_FILTERS_CONFIG.areaRadius,
    };
  }

  loadEmployeeMatchingList(
    id: string,
    params = {}
  ): Observable<CompanyMatchingResultListItemModel[]> {
    this.clearOutdatedCacheItems(MatchingTypeEnum.EMPLOYEE);
    this.filters = this.cleanFiltersData({ ...this.filters, ...params });
    this.setSessionValue(`employee-matching-filters-${this.employeeId}`, this.filters);

    const url = this.getStoreLink(
      MatchingTypeEnum.EMPLOYEE,
      this.employeeId,
      this.filters.freeText
    );
    const employeeMatchingCurrentListItem: CompanyMatchingResultListItemModel =
      this.getLocalStorageValue(url);

    return employeeMatchingCurrentListItem
      ? this.matchingApiService
          .getCompanyMatchingListById(employeeMatchingCurrentListItem.matchingId)
          .pipe(
            map(list => {
              const currentMatchingItemListIndex = list.findIndex(
                item => item.companyId === employeeMatchingCurrentListItem.companyId
              );

              return currentMatchingItemListIndex >= 0
                ? list.slice(currentMatchingItemListIndex)
                : list;
            })
          )
      : this.matchingApiService
          .getEmployeeMatchingList(id, this.filters)
          .pipe(map(resp => resp.content));
  }

  loadVacancyMatchingList(
    id: string,
    params = {}
  ): Observable<EmployeeMatchingResultListItemModel[]> {
    this.clearOutdatedCacheItems(MatchingTypeEnum.VACANCY);
    this.filters = this.cleanFiltersData({ ...this.filters, ...params });
    this.setSessionValue(`vacancy-matching-filters-${this.vacancyId}`, this.filters);
    const url = this.getStoreLink(MatchingTypeEnum.VACANCY, this.vacancyId);
    const vacancyMatchingCurrentListItem: EmployeeMatchingResultListItemModel =
      this.getLocalStorageValue(url);

    return vacancyMatchingCurrentListItem
      ? this.matchingApiService
          .getVacancyMatchingListById(vacancyMatchingCurrentListItem.matchingId)
          .pipe(
            map(list => {
              const currentMatchingItemListIndex = list.findIndex(
                item => item.employeeId === vacancyMatchingCurrentListItem.employeeId
              );

              return currentMatchingItemListIndex >= 0
                ? list.slice(currentMatchingItemListIndex)
                : list;
            }),
            catchError(() => {
              this.removeCurrentMatchingListItem(MatchingTypeEnum.VACANCY, this.vacancyId);
              return this.matchingApiService
                .getVacancyMatchingList(id, this.filters)
                .pipe(map(resp => resp.content));
            })
          )
      : this.matchingApiService
          .getVacancyMatchingList(id, this.filters)
          .pipe(map(resp => resp.content));
  }

  loadCompanyMatchingList(
    id: string,
    params = {}
  ): Observable<EmployeeMatchingResultListItemModel[]> {
    this.clearOutdatedCacheItems(MatchingTypeEnum.COMPANY);
    this.filters = this.cleanFiltersData({ ...this.filters, ...params });
    this.setSessionValue(`company-matching-filters-${this.companyId}`, this.filters);
    const url = this.getStoreLink(MatchingTypeEnum.COMPANY, this.companyId);
    const companyMatchingCurrentListItem: EmployeeMatchingResultListItemModel =
      this.getLocalStorageValue(url);

    return companyMatchingCurrentListItem
      ? this.matchingApiService
          .getEmployeeMatchingListById(companyMatchingCurrentListItem.matchingId)
          .pipe(
            map(list => {
              const currentMatchingItemListIndex = list.findIndex(
                item => item.employeeId === companyMatchingCurrentListItem.employeeId
              );

              return currentMatchingItemListIndex >= 0
                ? list.slice(currentMatchingItemListIndex)
                : list;
            }),
            catchError(() => {
              this.removeCurrentMatchingListItem(MatchingTypeEnum.COMPANY, this.companyId);
              return this.matchingApiService
                .getCompanyMatchingList(id, this.filters)
                .pipe(map(resp => resp.content));
            })
          )
      : this.matchingApiService
          .getCompanyMatchingList(id, this.filters)
          .pipe(map(resp => resp.content));
  }

  loadPseudoVacancyMatchingList(
    companyId: string,
    pseudoVacancyId: string,
    params = {}
  ): Observable<EmployeeMatchingResultListItemModel[]> {
    this.clearOutdatedCacheItems(MatchingTypeEnum.COMPETENCE);
    this.filters = this.cleanFiltersData({ ...this.filters, ...params });
    this.setSessionValue(`pseudo-vacancy-matching-filters-${this.pseudoVacancyId}`, this.filters);
    const url = this.getStoreLink(MatchingTypeEnum.COMPETENCE, this.pseudoVacancyId);
    const pseudoVacancyMatchingCurrentListItem: EmployeeMatchingResultListItemModel =
      this.getLocalStorageValue(url);

    return pseudoVacancyMatchingCurrentListItem
      ? this.matchingApiService
          .getEmployeeMatchingListById(pseudoVacancyMatchingCurrentListItem.matchingId)
          .pipe(
            map(list => {
              const currentMatchingItemListIndex = list.findIndex(
                item => item.employeeId === pseudoVacancyMatchingCurrentListItem.employeeId
              );

              return currentMatchingItemListIndex >= 0
                ? list.slice(currentMatchingItemListIndex)
                : list;
            }),
            catchError(() => {
              this.removeCurrentMatchingListItem(MatchingTypeEnum.COMPETENCE, this.pseudoVacancyId);
              return this.matchingApiService
                .getPseudoVacancyMatchingList(companyId, pseudoVacancyId, this.filters)
                .pipe(map(resp => resp.content));
            })
          )
      : this.matchingApiService
          .getPseudoVacancyMatchingList(companyId, pseudoVacancyId, this.filters)
          .pipe(map(resp => resp.content));
  }

  loadEmployeeInternalVacancies(
    employeeId: string,
    filters: EmployeeMatchingFiltersModel
  ): Observable<VacancyMatchingListItemModel[]> {
    return this.matchingApiService.getEmployeeInternalVacancies(
      employeeId,
      this.buildVacancyParams(filters)
    );
  }

  loadEmployeeExternalVacancies(
    employeeId: string,
    filters: EmployeeMatchingFiltersModel
  ): Observable<VacancyMatchingListItemModel[]> {
    return this.matchingApiService.getEmployeeExternalVacancies(
      employeeId,
      this.buildVacancyParams(filters)
    );
  }

  loadEmployeeMatchingCompanies(
    employeeId: string,
    filters: EmployeeMatchingFiltersModel
  ): Observable<CompanyMatchingListItemModel[]> {
    return this.matchingApiService.getEmployeeMatchingCompanies(
      employeeId,
      this.buildCompanyParams(filters)
    );
  }

  loadVacancyById(id: string): Observable<VacancyModel> {
    return this.vacanciesApiService.get(id);
  }

  loadEmployeeById(id: string): Observable<EmployeeModel> {
    return this.employeesApiService.get(id);
  }

  loadCompanyById(id: string): Observable<CompanyModel> {
    return this.companiesApiService.get(id);
  }

  loadEmployeeMatchResult(
    matchingId: string,
    companyId: string
  ): Observable<EmployeeMatchingResultModel> {
    return this.matchingApiService.getMatchingResult(matchingId, companyId);
  }

  updateCurrentEmployee(data: EmployeeModel): Observable<EmployeeModel> {
    return this.employeesApiService.update(data.uuid, data);
  }

  updateCurrentCompany(data: CompanyModel): Observable<CompanyModel> {
    return this.companiesApiService.update(data.uuid, data);
  }

  setIsLeadMatching(isLeadMatching: boolean): void {
    this.leadMatching = isLeadMatching;
  }

  isLeadMatching(): boolean {
    return this.leadMatching;
  }

  stopMatching(): void {
    this._inMatchingProcess.next(false);
    this.leadMatching = false;
    this.employeeId = null;
    this.employeeData = null;
    this.vacancyId = null;
    this.vacancyData = null;
    this.companyId = null;
    this.pseudoVacancyId = null;
    this.pseudoVacancyCompanyId = null;
    this.companyData = null;
    this.filters = { size: 100, page: 0 };
  }

  getLastMatchingLink(): string {
    const pathParts = ['matching'];

    if (this.employeeId) {
      pathParts.push(MatchingTypeEnum.EMPLOYEE, this.employeeId);
    } else if (this.vacancyId) {
      pathParts.push(MatchingTypeEnum.VACANCY, this.vacancyId);
    } else if (this.pseudoVacancyId && this.pseudoVacancyCompanyId) {
      pathParts.push(
        MatchingTypeEnum.COMPETENCE,
        this.pseudoVacancyId,
        'company',
        this.pseudoVacancyCompanyId
      );
    } else if (this.companyId) {
      pathParts.push(MatchingTypeEnum.COMPANY, this.companyId);
    } else {
      return this.getLocalStorageValue(this.lastMatchingPathKey);
    }

    return pathParts.join('/');
  }

  setSessionValue(key: string, value: any): void {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  getSessionValue(key: string): any | null {
    return JSON.parse(sessionStorage.getItem(key));
  }

  setLocalStorageValue(key: string, value: unknown): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  getLocalStorageValue<T>(key: string): T {
    return JSON.parse(localStorage.getItem(key)) as T;
  }

  removeCurrentMatchingListItem(entityType: MatchingTypeEnum, entityId: string): void {
    const cacheLinksUrl = this.matchingEntityCacheLinksKeys[entityType];
    const cacheLinksItem = this.getLocalStorageValue(cacheLinksUrl);

    if (cacheLinksItem && cacheLinksItem[entityId]) {
      Object.keys(cacheLinksItem[entityId]).forEach(sub =>
        this.removeCurrentMatchingListItemRecord(entityType, entityId, sub)
      );
      delete cacheLinksItem[entityId];
      this.setLocalStorageValue(cacheLinksUrl, cacheLinksItem);
    }

    this.removeCurrentMatchingListItemRecord(entityType, entityId);
  }

  removeCurrentMatchingListItemRecord(
    entityType: MatchingTypeEnum,
    entityId: string,
    entitySubId?: string
  ): void {
    localStorage.removeItem(this.getStoreLink(entityType, entityId, entitySubId));
  }

  storeLastMatchingPath(path: string): void {
    this.setLocalStorageValue(this.lastMatchingPathKey, path);
  }

  storeCurrentMatchingListItem(
    entityType: MatchingTypeEnum,
    listItem: EmployeeMatchingResultListItemModel | CompanyMatchingResultListItemModel,
    entityId: string,
    entitySubId?: string
  ): void {
    this.setLocalStorageValue(this.getStoreLink(entityType, entityId, entitySubId), listItem);
    this.storeMatchingCacheItem(entityType, entityId, entitySubId);
  }

  private cleanFiltersData(filters: EmployeeMatchingFiltersModel): EmployeeMatchingFiltersModel {
    Object.keys(filters).forEach(k => {
      if (filters[k] === null) {
        delete filters[k];
      }
    });
    return filters;
  }

  private getAreaSpeed(type: string): number {
    switch (type) {
      case 'BICYCLE':
        return 14;
      case 'MOPED':
        return 30;
      case 'PUBLIC_TRANSPORT':
      case 'PEDELEC':
        return 16;
      case 'MOTO':
      case 'CAR':
        return 36;
      default:
        return 5;
    }
  }

  private getEmployeeAreaRadius(): number {
    const values = (this.employeeData?.transports || []).map(transport => {
      return transport.transportCode === 'NONE'
        ? DEFAULT_MATCHING_FILTERS_CONFIG.areaRadius
        : 1000 * Math.round((+transport.radius / 60) * this.getAreaSpeed(transport.transportCode));
    });
    return Math.max(500, ...values);
  }

  private getEmployeePostalCodes(): string[] {
    return this.authService.isSalesAdmin(this.currentUserPayload)
      ? []
      : this.currentUserPayload.offices.filter(o => o.postalCodes?.length).map(o => o.shortName);
  }

  private getEmployeeConstructStatus(): string[] {
    return this.currentUserPayload.user.niche === 'JFX_CONSTRUCT'
      ? ['CONSTRUCT']
      : ['NOT_CONSTRUCT'];
  }

  private getStoreLink(
    entityType: MatchingTypeEnum,
    entityId: string,
    entitySubId: string = null
  ): string {
    const urlParts = [this.matchingEntityListItemKeys[entityType], entityId, entitySubId];
    return (entitySubId ? urlParts : urlParts.slice(0, 2)).join('-');
  }

  private storeMatchingCacheItem(
    entityType: MatchingTypeEnum,
    entityId: string,
    entitySubId: string = null
  ): void {
    const cacheUrl = this.matchingEntityCacheKeys[entityType];
    const cacheItem = this.getLocalStorageValue(cacheUrl) || {};
    cacheItem[entityId] = Date.now();

    const itemsToSave = Object.entries(cacheItem).sort((a, b) => b[1] - a[1]);
    const itemsToRemove = itemsToSave.splice(3);

    this.setLocalStorageValue(
      cacheUrl,
      itemsToSave.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    );

    if (entitySubId) {
      const cacheLinksUrl = this.matchingEntityCacheLinksKeys[entityType];
      const cacheLinksItem = this.getLocalStorageValue(cacheLinksUrl) || {};
      cacheLinksItem[entityId] = cacheLinksItem[entityId] || {};
      cacheLinksItem[entityId][entitySubId] = 1;
      this.setLocalStorageValue(cacheLinksUrl, cacheLinksItem);
    }

    itemsToRemove.forEach(([key]) => this.removeCurrentMatchingListItem(entityType, key));
  }

  private buildVacancyParams(filters: EmployeeMatchingFiltersModel): Record<string, unknown> {
    return this.pickParams(filters, [
      'shiftRegime',
      'drivingLicense',
      'areaRadius',
      'isConstructStatus',
      'salesRegion',
      'size',
    ]);
  }

  private buildCompanyParams(filters: EmployeeMatchingFiltersModel): Record<string, unknown> {
    return this.pickParams(filters, ['areaRadius', 'isConstructStatus', 'salesRegion', 'size']);
  }

  private pickParams(
    filters: EmployeeMatchingFiltersModel,
    keys: (keyof EmployeeMatchingFiltersModel)[]
  ): Record<string, unknown> {
    return Object.fromEntries(
      keys
        .filter(key =>
          Array.isArray(filters[key]) ? (filters[key] as unknown[]).length > 0 : !!filters[key]
        )
        .map(key => [key, filters[key]])
    );
  }

  private clearOutdatedCacheItems(entityType: MatchingTypeEnum): void {
    const delta = DebounceConstants.Hour * 24;
    const cacheUrl = this.matchingEntityCacheKeys[entityType];
    const cacheItem = this.getLocalStorageValue(cacheUrl) || {};

    Object.entries(cacheItem)
      .filter(([key, value]) => Date.now() - value > delta)
      .forEach(([key]) => {
        this.removeCurrentMatchingListItem(entityType, key);
        delete cacheItem[key];
      });
    this.setLocalStorageValue(cacheUrl, cacheItem);
  }
}
